'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Check } from 'lucide-react';

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface Props {
  onSuccess: (email: string) => void;
  switchToLogin: () => void;
}

export default function SignupForm({ onSuccess, switchToLogin }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>();

  const password = watch('password', '');

  const passwordStrength = (pwd: string): { score: number; label: string; color: string } => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const map: Record<number, { label: string; color: string }> = {
      0: { label: '', color: '' },
      1: { label: 'Weak', color: 'bg-red-400' },
      2: { label: 'Fair', color: 'bg-amber-400' },
      3: { label: 'Good', color: 'bg-blue-400' },
      4: { label: 'Strong', color: 'bg-green-500' },
    };
    return { score, ...map[score] };
  };

  const strength = passwordStrength(password);

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    // BACKEND: POST /api/auth/register with { firstName, lastName, email, password }
    await new Promise((r) => setTimeout(r, 1600));
    toast.success('Account created! Welcome to IvyDirectory.');
    setIsLoading(false);
    onSuccess(data.email);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 animate-slide-up">
      <div>
        <h2 className="font-display text-2xl font-semibold text-navy mb-1">Create your account</h2>
        <p className="text-sm text-[#5a6e96]">Join thousands of students researching Ivy League schools</p>
      </div>

      {/* Social auth */}
      <div className="grid grid-cols-2 gap-3">
        {['Google', 'Apple'].map((provider) => (
          <button
            key={`signup-social-${provider}`}
            type="button"
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-blue-100 rounded-xl text-sm font-medium text-navy hover:bg-blue-50 hover:border-navy/20 transition-all duration-150 active:scale-95"
          >
            {provider === 'Google' ? (
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
              </svg>
            )}
            {provider}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <hr className="flex-1 border-blue-100" />
        <span className="text-xs text-[#5a6e96] font-medium">or with email</span>
        <hr className="flex-1 border-blue-100" />
      </div>

      {/* Name row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-navy mb-1.5" htmlFor="firstName">First name</label>
          <input
            id="firstName"
            type="text"
            placeholder="Alexandra"
            className={`w-full px-4 py-3 rounded-xl border bg-white text-navy placeholder-[#8fa3c8] text-sm transition-all duration-150 outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy ${errors.firstName ? 'border-red-400' : 'border-blue-100 hover:border-navy/30'}`}
            {...register('firstName', { required: 'Required' })}
          />
          {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-navy mb-1.5" htmlFor="lastName">Last name</label>
          <input
            id="lastName"
            type="text"
            placeholder="Chen"
            className={`w-full px-4 py-3 rounded-xl border bg-white text-navy placeholder-[#8fa3c8] text-sm transition-all duration-150 outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy ${errors.lastName ? 'border-red-400' : 'border-blue-100 hover:border-navy/30'}`}
            {...register('lastName', { required: 'Required' })}
          />
          {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>}
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5" htmlFor="signup-email">Email address</label>
        <input
          id="signup-email"
          type="email"
          placeholder="you@highschool.edu"
          className={`w-full px-4 py-3 rounded-xl border bg-white text-navy placeholder-[#8fa3c8] text-sm transition-all duration-150 outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy ${errors.email ? 'border-red-400' : 'border-blue-100 hover:border-navy/30'}`}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
          })}
        />
        {errors.email && <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5" htmlFor="signup-password">Password</label>
        <div className="relative">
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Min. 8 characters"
            className={`w-full px-4 py-3 pr-12 rounded-xl border bg-white text-navy placeholder-[#8fa3c8] text-sm transition-all duration-150 outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy ${errors.password ? 'border-red-400' : 'border-blue-100 hover:border-navy/30'}`}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'At least 8 characters required' },
            })}
          />
          <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a6e96] hover:text-navy transition-colors p-1" aria-label="Toggle password visibility">
            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {password && (
          <div className="mt-2 flex items-center gap-2">
            <div className="flex gap-1 flex-1">
              {[1, 2, 3, 4].map((i) => (
                <div key={`strength-bar-${i}`} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i <= strength.score ? strength.color : 'bg-blue-100'}`} />
              ))}
            </div>
            {strength.label && <span className="text-xs text-[#5a6e96]">{strength.label}</span>}
          </div>
        )}
        {errors.password && <p className="mt-1.5 text-xs text-red-500">{errors.password.message}</p>}
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5" htmlFor="confirm-password">Confirm password</label>
        <div className="relative">
          <input
            id="confirm-password"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Re-enter your password"
            className={`w-full px-4 py-3 pr-12 rounded-xl border bg-white text-navy placeholder-[#8fa3c8] text-sm transition-all duration-150 outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy ${errors.confirmPassword ? 'border-red-400' : 'border-blue-100 hover:border-navy/30'}`}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (val) => val === password || 'Passwords do not match',
            })}
          />
          <button type="button" onClick={() => setShowConfirm((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a6e96] hover:text-navy transition-colors p-1" aria-label="Toggle confirm password visibility">
            {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="mt-1.5 text-xs text-red-500">{errors.confirmPassword.message}</p>}
      </div>

      {/* Terms */}
      <div className="flex items-start gap-2.5">
        <input
          id="terms"
          type="checkbox"
          className="mt-0.5 w-4 h-4 rounded border-blue-200 accent-navy cursor-pointer"
          {...register('terms', { required: 'You must accept the terms to continue' })}
        />
        <label htmlFor="terms" className="text-sm text-[#5a6e96] cursor-pointer leading-relaxed">
          I agree to the{' '}
          <span className="text-navy underline underline-offset-2 hover:text-blue-600 cursor-pointer">Terms of Service</span>
          {' '}and{' '}
          <span className="text-navy underline underline-offset-2 hover:text-blue-600 cursor-pointer">Privacy Policy</span>
        </label>
      </div>
      {errors.terms && <p className="text-xs text-red-500">{errors.terms.message}</p>}

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3.5 bg-navy text-white rounded-xl font-medium text-sm hover:bg-navy-light disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.99] flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
        style={{ minHeight: '48px' }}
      >
        {isLoading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Creating account...
          </>
        ) : (
          <>
            <Check size={16} />
            Create Free Account
          </>
        )}
      </button>

      <p className="text-center text-sm text-[#5a6e96]">
        Already have an account?{' '}
        <button type="button" onClick={switchToLogin} className="text-navy font-medium hover:text-blue-600 underline underline-offset-2 transition-colors">
          Sign in
        </button>
      </p>
    </form>
  );
}